import React, { useEffect, useState } from 'react';
import { Screen } from '../App';
import { User } from '../storage';
import * as authApi from '../authApi';
import { isValidEmail } from '../util/email';

interface Props { navigate: (s: Screen) => void; user: User | null; }

// "Accept and get invoiced" page, reached from the quote email (/accept?lead=<id>).
// Confirms billing details, then the server creates a net-30 Stripe invoice.
export default function Accept({ navigate, user }: Props) {
  const home: Screen = user ? 'dashboard' : 'landing';
  const leadId = new URLSearchParams(window.location.search).get('lead') || '';

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<authApi.AcceptSummary | null>(null);
  const [loadErr, setLoadErr] = useState('');

  const [billingName, setBillingName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let active = true;
    if (!leadId) { setLoadErr('This link is missing its quote reference.'); setLoading(false); return; }
    authApi.acceptSummary(leadId)
      .then((s) => { if (active) setSummary(s); })
      .catch((e: any) => { if (active) setLoadErr(e?.message || 'Could not load this quote.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [leadId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    if (!billingName.trim()) { setErr('Please enter a billing name.'); return; }
    if (!isValidEmail(billingEmail)) { setErr('Please enter a valid billing email.'); return; }
    setBusy(true);
    try {
      await authApi.acceptSubmit({ lead: leadId, billingName: billingName.trim(), billingEmail: billingEmail.trim(), poNumber: poNumber.trim() || undefined });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (e: any) {
      setErr(e?.message || 'We could not create the invoice just now. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate(home)} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>Back
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>Accept your quote</span>
        <div style={{ width: 60 }} />
      </div>

      <div className="page-content" style={{ paddingTop: 28 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}><div className="spinner" /></div>
        ) : loadErr ? (
          <div className="card" style={{ textAlign: 'center', padding: '32px 20px' }}>
            <p style={{ fontSize: 32, marginBottom: 12 }}>🔎</p>
            <h2 style={{ marginBottom: 8 }}>We could not open this quote</h2>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 20 }}>{loadErr}</p>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Please reach out to support@decodemystudent.com and we will help.</p>
          </div>
        ) : summary?.alreadyActive ? (
          <div className="card" style={{ textAlign: 'center', padding: '32px 20px' }}>
            <p style={{ fontSize: 36, marginBottom: 12 }}>✅</p>
            <h2 style={{ marginBottom: 8 }}>This license is already active</h2>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>Your school is all set. Check your email for the join code, or contact support@decodemystudent.com.</p>
          </div>
        ) : submitted ? (
          <div className="card" style={{ textAlign: 'center', padding: '32px 20px' }}>
            <p style={{ fontSize: 36, marginBottom: 12 }}>🎉</p>
            <h2 style={{ marginBottom: 8 }}>Thank you</h2>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 8 }}>
              We sent an invoice to <strong>{billingEmail.trim()}</strong>, due within 30 days.
            </p>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              As soon as it is paid, your school license activates automatically and we email you the join code for your teachers.
            </p>
          </div>
        ) : summary ? (
          <>
            <h1 style={{ fontSize: 24, marginBottom: 8 }}>Accept and get invoiced</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              Confirm your billing details and we will send an invoice, due within 30 days. Your license activates the moment it is paid.
            </p>

            {/* Quote summary */}
            <div className="card" style={{ marginBottom: 20, background: 'var(--teal-light)', border: '1px solid var(--teal)' }}>
              <p style={{ fontWeight: 700, color: 'var(--teal)', marginBottom: 8 }}>{summary.schoolName}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 14, color: 'var(--charcoal)' }}>{summary.tierLabel}</span>
                <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--teal)' }}>{summary.priceLabel}<span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-secondary)' }}> / year</span></span>
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.5 }}>
                Full building access for every teacher, 12 months, one invoice. Fundable through Title IV-A.
              </p>
            </div>

            <div className="card">
              <form onSubmit={submit}>
                <div className="form-group">
                  <label>Billing name *</label>
                  <input type="text" value={billingName} onChange={e => setBillingName(e.target.value)} placeholder="Name on the invoice" />
                </div>
                <div className="form-group">
                  <label>Billing email *</label>
                  <input type="email" value={billingEmail} onChange={e => setBillingEmail(e.target.value)} placeholder="billing@school.org" />
                  <p className="form-hint">We send the invoice here.</p>
                </div>
                <div className="form-group">
                  <label>PO number (if you have one)</label>
                  <input type="text" value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="Optional" />
                </div>
                {err && <div style={{ background: 'var(--coral-light)', color: 'var(--coral)', padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 12 }}>{err}</div>}
                <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Sending the invoice…' : 'Accept and send my invoice'}</button>
              </form>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
